<script lang="ts">
	import type { ISbRichtext } from '@storyblok/js';
	import type { HTMLAttributes } from 'svelte/elements';
	import { onMount } from 'svelte';
	import { toString } from '$lib/utils/storyblok/richtext';
	import { fly } from 'svelte/transition';
	import { innerWidth } from 'svelte/reactivity/window';

	interface Props extends HTMLAttributes<HTMLDivElement> {
		items: Array<ISbRichtext>;
		delay?: number;
	}

	let { items, delay = 2500, ...other }: Props = $props();

	let previous = $state<number | null>(null);
	let active = $state(0);

	onMount(() => {
		const interval = setInterval(() => {
			previous = active;
			active = (active + 1) % items.length;
		}, delay);

		return () => clearInterval(interval);
	});

	let previousLines = $derived(previous === null ? [] : toString(items[previous]).split('\n'));
	let currentLines = $derived(toString(items[active]).split('\n'));
</script>

<div {...other}>
	<div class="grid lg:gap-y-1">
		{#if previous !== null}
			{#each previousLines as line, i}
				{#key line}
					<div
						class={['fade-previous-lines col-span-full', { hidden: line === currentLines[i] }]}
						style="grid-row: {i + 1}"
					>
						{line}
					</div>
				{/key}
			{/each}
		{/if}
		{#each currentLines as line, i}
			{@const baseValue = !innerWidth.current || innerWidth.current <= 1024 ? 16 : 32}
			{#key line}
				<div
					class="col-span-full"
					style="grid-row: {i + 1}"
					in:fly={{ y: i === 0 ? baseValue : baseValue * (1.5 * i), duration: 420, opacity: 0 }}
				>
					{line}
				</div>
			{/key}
		{/each}
	</div>
</div>

<style lang="scss">
	.fade-previous-lines {
		animation: fadeOut 0.42s ease-in-out forwards;
	}

	@keyframes fadeOut {
		from {
			opacity: 1;
		}

		to {
			opacity: 0;
			transform: translateY(-8px);
		}
	}
</style>
